import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";
import { MonitorSmartphone, RefreshCw, ShieldOff, ShieldCheck, AlertTriangle } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

/** Devices & Sync monitoring panel — used by Subscriber Settings and Super Admin. */
export default function DeviceSyncPanel({ basePath }) {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`${basePath}/devices`)
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [basePath]);

  useEffect(() => { load(); }, [load]);

  const setRevoked = async (deviceId, revoke) => {
    setBusy(deviceId);
    try {
      await api.post(`${basePath}/devices/${deviceId}/${revoke ? "revoke" : "restore"}`);
      toast.success(revoke ? t("devices.revokedToast") : t("devices.restoredToast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("msg.saveFailed"));
    } finally { setBusy(null); }
  };

  return (
    <div className="am-card p-6 mt-6" data-testid="device-sync-panel">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-2xl bg-accent grid place-items-center shrink-0">
          <MonitorSmartphone className="w-5 h-5 text-primary" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-xl font-bold leading-tight">{t("devices.title")}</h2>
          <p className="text-xs text-muted-foreground">{t("devices.subtitle")}</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full" onClick={load} disabled={loading} data-testid="devices-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> {t("devices.refresh")}
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b">
              <th className="pb-3 pr-4">{t("devices.user")}</th>
              <th className="pb-3 pr-4">{t("devices.device")}</th>
              <th className="pb-3 pr-4">{t("devices.lastSync")}</th>
              <th className="pb-3 pr-4">{t("devices.pending")}</th>
              <th className="pb-3 pr-4">{t("common.status")}</th>
              <th className="pb-3">{t("devices.action")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.device_id} className="border-b last:border-0" data-testid={`device-row-${d.device_id}`}>
                <td className="py-3 pr-4">
                  <div className="font-semibold">{d.user_name || "—"}</div>
                  <div className="text-xs text-muted-foreground capitalize">{d.role || ""}</div>
                </td>
                <td className="py-3 pr-4">
                  <div className="font-mono text-xs">{(d.device_id || "").slice(0, 8)}…</div>
                  <div className="text-[11px] text-muted-foreground truncate max-w-[180px]" title={d.ua}>{d.ua || ""}</div>
                </td>
                <td className="py-3 pr-4">
                  <div className="text-xs">{fmt(d.last_sync_at)}</div>
                  {d.last_sync_error && (
                    <div className="text-[11px] text-red-700 inline-flex items-center gap-1 max-w-[200px] truncate" title={d.last_sync_error}>
                      <AlertTriangle className="w-3 h-3 shrink-0" /> {d.last_sync_error}
                    </div>
                  )}
                </td>
                <td className="py-3 pr-4">
                  <span className="font-mono font-bold">{d.pending_count ?? 0}</span>
                  {(d.failed_count ?? 0) > 0 && <span className="ml-1.5 text-xs text-red-700 font-semibold">({d.failed_count} {t("devices.failedShort")})</span>}
                </td>
                <td className="py-3 pr-4">
                  <span className={`am-chip ${d.revoked ? "bg-red-100 text-red-800" : ""}`}>
                    {d.revoked ? t("devices.revoked") : t("common.active")}
                  </span>
                </td>
                <td className="py-3">
                  {d.revoked ? (
                    <Button size="sm" variant="outline" className="rounded-full" disabled={busy === d.device_id}
                      onClick={() => setRevoked(d.device_id, false)} data-testid={`device-restore-${d.device_id}`}>
                      <ShieldCheck className="w-4 h-4 mr-1" /> {t("devices.restore")}
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" className="rounded-full text-red-700 hover:text-red-800" disabled={busy === d.device_id}
                      onClick={() => setRevoked(d.device_id, true)} data-testid={`device-revoke-${d.device_id}`}>
                      <ShieldOff className="w-4 h-4 mr-1" /> {t("devices.revoke")}
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">{t("devices.none")}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
